import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BLOG, CONTACT, RDV, REALISATIONS, SERVICES, blogArticle } from "../config/routes";

import "./styles/Blog.css";

const CATS = {
  ALL: "All",
  WEB: "Web",
  SAAS: "SaaS",
  MOBILE: "Mobile",
  GROWTH: "SEO/SEA/Tracking",
  PRODUCT: "Produit",
};

function cn(...x) {
  return x.filter(Boolean).join(" ");
}

function guessCategory(article) {
  const t = String(article?.title || "").toLowerCase();
  if (article?.category) return article.category;
  if (t.includes("seo") || t.includes("sea") || t.includes("ads") || t.includes("ga4") || t.includes("tracking")) return CATS.GROWTH;
  if (t.includes("saas") || t.includes("api") || t.includes("auth") || t.includes("stripe") || t.includes("supabase")) return CATS.SAAS;
  if (t.includes("flutter") || t.includes("react native") || t.includes("ios") || t.includes("android")) return CATS.MOBILE;
  if (t.includes("landing") || t.includes("conversion") || t.includes("site") || t.includes("vitrine") || t.includes("next") || t.includes("react")) return CATS.WEB;
  if (t.includes("mvp") || t.includes("roadmap") || t.includes("product") || t.includes("kpi")) return CATS.PRODUCT;
  return CATS.PRODUCT;
}

export default function Blog() {
  const initialFetchCount = 12;
  const displayIncrement = 6;

  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 599);

  const [articles, setArticles] = useState([]);
  const [visibleCount, setVisibleCount] = useState(displayIncrement);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [activeCat, setActiveCat] = useState(CATS.ALL);
  const [q, setQ] = useState("");

  const callRoute = RDV;
  const metaTitle = "Insights - Dreamer Agency";
  const metaDesc =
    "Articles et guides sur le dev web, SaaS, mobile, SEO/SEA/Tracking et la construction de produits. Approche pragmatique orientée delivery & conversion.";

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();
    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }
    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".blog2026");
    if (!mainContainer) return;
    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  const loadArticles = async (offset, limit) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        setLoadError("REACT_APP_API_URL manquant. Impossible de charger les articles.");
        setLoading(false);
        return;
      }

      const res = await axios.get(`${apiUrl}/articles-list`, {
        params: { limit, offset },
      });

      const incoming = Array.isArray(res?.data) ? res.data : [];
      setArticles((prev) => [...prev, ...incoming]);
      setLoadError("");
    } catch (e) {
      setLoadError("Erreur lors du chargement des articles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadArticles(0, initialFetchCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowMore = () => {
    const newVisibleCount = visibleCount + displayIncrement;
    setVisibleCount(newVisibleCount);

    if (newVisibleCount >= articles.length) {
      loadArticles(articles.length, displayIncrement);
    }
  };

  const normalized = useMemo(() => {
    return articles.map((a) => ({
      ...a,
      _cat: guessCategory(a),
      _titleText: String(a?.title || "").replace(/<[^>]*>/g, ""),
    }));
  }, [articles]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return normalized
      .filter((a) => {
        if (activeCat === CATS.ALL) return true;
        return a._cat === activeCat;
      })
      .filter((a) => {
        if (!query) return true;
        const hay = `${a._titleText} ${a._cat}`.toLowerCase();
        return hay.includes(query);
      });
  }, [normalized, activeCat, q]);

  const topPicks = useMemo(() => filtered.slice(0, 3), [filtered]);
  const listItems = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  const jsonLd = useMemo(() => {
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Dreamer Agency Insights",
      itemListElement: normalized.slice(0, 10).map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: a._titleText || `Article ${i + 1}`,
        url: `https://dreameragency.fr${blogArticle(a.id)}`,
      })),
    };
    return [itemList];
  }, [normalized]);

  const categories = useMemo(
    () => [CATS.ALL, CATS.WEB, CATS.SAAS, CATS.MOBILE, CATS.GROWTH, CATS.PRODUCT],
    []
  );

  return (
    <main className={`blog2026 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={`https://dreameragency.fr${BLOG}`} />

        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dreameragency.fr${BLOG}`} />
        <meta property="og:image" content="https://dreameragency.fr/media/OG/blog_background.png" />
      </Helmet>

      <section className="blog2026-hero">
        <div className="blog2026-container">
          <div className="blog2026-heroGrid">
            <div className="blog2026-heroLeft">
              <p className="blog2026-eyebrow">Insights</p>
              <h1 className="blog2026-h1">Des contenus utiles, orientés livraison.</h1>
              <p className="blog2026-lead">
                Guides et articles sur <strong>Web</strong>, <strong>SaaS</strong>, <strong>Mobile</strong> et{" "}
                <strong>Growth</strong> (SEO/SEA/Tracking).  
                Objectif : te faire gagner du temps, éviter les erreurs, et accélérer ton projet.
              </p>

              <div className="blog2026-actions">
                <Link to={callRoute} className="btn btn-primary">Réserver un appel</Link>
                <Link to={SERVICES} className="btn btn-secondary">Voir les services</Link>
                <Link to={REALISATIONS} className="btn btn-ghost">Réalisations</Link>
              </div>

              <div className="blog2026-controls">
                <div className="blog2026-filters">
                  {categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={cn("chipBtn", activeCat === c && "active")}
                      onClick={() => {
                        setActiveCat(c);
                        setVisibleCount(displayIncrement);
                      }}
                    >
                      {c}
                    </button>
                  ))}
                </div>

                <div className="blog2026-search">
                  <input
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setVisibleCount(displayIncrement);
                    }}
                    placeholder="Rechercher : GA4, Flutter, SaaS, conversion…"
                    aria-label="Rechercher un article"
                  />
                </div>
              </div>
            </div>

            <div className="blog2026-heroRight">
              <div className="blog2026-sideCard">
                <p className="sideTitle">Vous voulez qu’on le fasse pour vous ?</p>
                <p className="blog2026-muted">
                  Le blog aide à comprendre. Mais si tu veux avancer vite : on cadre ton MVP et on livre.
                </p>
                <Link to={callRoute} className="btn btn-primary btn-block">Réserver un appel</Link>
                <Link to={CONTACT} className="btn btn-ghost btn-block">Contact</Link>
              </div>

              <div className="blog2026-sideCard blog2026-sideCardSoft">
                <p className="sideTitle">Top picks</p>
                <div className="topList">
                  {topPicks.map((a) => (
                    <Link key={a.id} to={blogArticle(a.id)} className="topItem">
                      <span className="topCat">{a._cat}</span>
                      <span className="topTitle">{a._titleText || "Article"}</span>
                    </Link>
                  ))}
                  {topPicks.length === 0 ? (
                    <p className="blog2026-muted">Aucun article trouvé.</p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {loadError ? <div className="blog2026-error">{loadError}</div> : null}
          {loading ? <div className="blog2026-loading">Chargement…</div> : null}
        </div>
      </section>

      <section className="blog2026-section">
        <div className="blog2026-container">
          <div className="blog2026-grid">
            {listItems.map((a) => (
              <article key={a.id} className="postCard">
                <Link to={blogArticle(a.id)} className="postMedia">
                  {a.thumbnail_url ? (
                    <img src={a.thumbnail_url} alt={a._titleText || "Article"} loading="lazy" />
                  ) : (
                    <div className="postPlaceholder" aria-hidden="true">
                      <span>{a._cat}</span>
                    </div>
                  )}
                  {a.date ? <div className="postDate">{a.date}</div> : null}
                </Link>

                <div className="postBody">
                  <div className="postMeta">
                    <span className="postTag">{a._cat}</span>
                    {a.read_time ? <span className="postMuted">{a.read_time}</span> : null}
                  </div>

                  <h2 className="postTitle">{a._titleText}</h2>

                  <div className="postActions">
                    <Link to={blogArticle(a.id)} className="btn btn-tertiary">Lire →</Link>
                    <Link to={RDV} className="link">Parler du projet →</Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="blog2026-more">
            {visibleCount < filtered.length ? (
              <button className="btn btn-secondary" onClick={handleShowMore}>
                Voir plus
              </button>
            ) : null}
          </div>

          <div className="blog2026-cta">
            <div>
              <h2 className="blog2026-h2">Besoin d’un avis rapide ?</h2>
              <p className="blog2026-muted">
                Réserve un appel : on clarifie ton besoin, le scope MVP, et la meilleure approche.
              </p>
            </div>
            <div className="blog2026-ctaActions">
              <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
              <Link to={SERVICES} className="btn btn-ghost">Voir les services</Link>
            </div>
          </div>
        </div>
      </section>

      {jsonLd.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </main>
  );
}
